import { uiText, useUiLanguage } from '@/i18n/interface';
import { useState } from "react";
import { UserPlus } from "lucide-react";
import { FunctionsHttpError } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface BeltLevel {
  id: string;
  name: string;
}

interface AddManagedStudentDialogProps {
  beltLevels: BeltLevel[];
  onCreated: (studentId: string) => Promise<void>;
}

const createInitialForm = () => ({
  first_name: "",
  last_name: "",
  phone: "",
  date_of_birth: "",
  join_date: new Date().toISOString().slice(0, 10),
  belt_level_id: "",
  address: "",
  city: "",
  postal_code: "",
});

export default function AddManagedStudentDialog({
  beltLevels,
  onCreated,
}: AddManagedStudentDialogProps) {
  useUiLanguage();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(createInitialForm);

  const updateField = (field: string, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!form.first_name.trim() || !form.last_name.trim()) {
      toast({
        title: uiText("Missing student name"),
        description: uiText("First name and last name are required."),
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);

    const {
      data: { session },
      error: sessionError,
    } = await supabase.auth.getSession();

    if (sessionError || !session?.access_token) {
      setSubmitting(false);
      toast({
        title: uiText("Student could not be added"),
        description: uiText("Your session has expired. Please sign in again."),
        variant: "destructive",
      });
      return;
    }

    const { data, error } = await supabase.functions.invoke(
      "create-managed-student",
      {
        body: form,
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      },
    );

    setSubmitting(false);

    if (error || data?.error) {
      let description = data?.error || error?.message;

      if (error instanceof FunctionsHttpError) {
        try {
          const responseBody = await error.context.json();
          description = responseBody?.error || responseBody?.message || description;
        } catch {
          // Keep the SDK error message when the response body is not JSON.
        }
      }

      toast({
        title: uiText("Student could not be added"),
        description:
          description ||
          "An unexpected error occurred while adding the student.",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: uiText("Student added"),
      description: uiText("{{value0}} {{value1}} can now be tracked without a login.", { value0: form.first_name, value1: form.last_name }),
    });

    setOpen(false);
    setForm(createInitialForm());
    await onCreated(data.id);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="w-full sm:w-auto">
          <UserPlus className="mr-2 h-4 w-4" />
          {uiText("Add student without email")}</Button>
      </DialogTrigger>

      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{uiText("Add a managed student")}</DialogTitle>
            <DialogDescription>
              {uiText("This student will appear in payments, attendance, and progression, but will not be able to sign in.")}</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-5 sm:grid-cols-2">
            <Field label={uiText("First name")} required value={form.first_name} onChange={(value) => updateField("first_name", value)} />
            <Field label={uiText("Last name")} required value={form.last_name} onChange={(value) => updateField("last_name", value)} />
            <Field label={uiText("Phone")} value={form.phone} onChange={(value) => updateField("phone", value)} />
            <Field label={uiText("Date of birth")} type="date" value={form.date_of_birth} onChange={(value) => updateField("date_of_birth", value)} />
            <Field label={uiText("Join date")} type="date" value={form.join_date} onChange={(value) => updateField("join_date", value)} />

            <div className="space-y-2">
              <Label>{uiText("Belt level")}</Label>
              <Select
                value={form.belt_level_id || "none"}
                onValueChange={(value) => updateField("belt_level_id", value === "none" ? "" : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={uiText("No belt assigned")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{uiText("No belt assigned")}</SelectItem>
                  {beltLevels.map((belt) => (
                    <SelectItem key={belt.id} value={belt.id}>
                      {belt.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Field label={uiText("Address")} value={form.address} onChange={(value) => updateField("address", value)} />
            <Field label={uiText("City")} value={form.city} onChange={(value) => updateField("city", value)} />
            <Field label={uiText("Postal code")} value={form.postal_code} onChange={(value) => updateField("postal_code", value)} />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={submitting}>
              {uiText("Cancel")}</Button>
            <Button type="submit" disabled={submitting || !form.first_name.trim() || !form.last_name.trim()}>
              {submitting ? uiText("Adding…") : uiText("Add student")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface FieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  required?: boolean;
}

function Field({ label, value, onChange, type = "text", required = false }: FieldProps) {
  useUiLanguage();
  const id = `managed-${label.toLowerCase().replace(/\s+/g, "-")}`;

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type={type}
        required={required}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}
